import { css } from '@emotion/css';
import { Button, useMediaQuery } from '@mui/material';
import Skeleton from 'atoms/Skeleton';
import { eligibilityQuestionnaireJSON } from 'config/questionnaire';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Converter } from 'showdown';
import { loadingEnd, loadingStart, setViewStateErrorCode, setViewStateErrorText } from 'store/globalSlice';
import { RootState } from 'store/store';
import { setParticipantDetails, setParticipantViewState } from 'store/userSlice';
import { colors } from 'styles/colors';
import * as SurveyCore from 'survey-core';
import { Model } from 'survey-core';
import 'survey-core/defaultV2.min.css';
import { SurveyPDF } from 'survey-pdf';
import { Survey } from 'survey-react-ui';
import * as widgets from 'surveyjs-widgets';
import { nouislider } from 'surveyjs-widgets';
import { ParticipantAPI } from 'utils/ParticipantAPI';
import { QuestionnaireAPI } from 'utils/QuestionnaireAPI';

import { light_theme_survey } from 'utils/theme_light_survey';
import { ViewState } from 'utils/viewstate';

const OverloadEligibleText = () => (
    <p>
        Mange tak! Baseret på dine svar kan vi se, at du kan deltage i Projekt PLANETDIET.
        <br />
        Du vil sandsynligvis opleve, at der ikke er nogen ledige tider i bookingsystemet. Dette betyder IKKE, at vi har
        alle de deltagere vi skal bruge, men blot at alle vores tider er booket.
        <br />
        <br />
        Vi vil åbne op for nye tider til informationssamtaler i januar 2026.
        <br />
        Vi sender dig en påmindelse i din digitale post til december, med et link til bookingsiden.
        <br />
        <br />
        Vi glæder os til at se dig i Projekt PLANETDIET!
    </p>
);

const isOverload = process.env.REACT_APP_OVERLOAD_FLAG === 'true';

const wrapInParagraphWithStyle = (text: string) => {
    // Check if the input text contains the <p> tag
    if (!text.includes('<p>')) {
        // If not, add the <p> tag with the specified styles
        text = `<p style="font-weight: normal">${text}</p>`;
    } else {
        // If the <p> tag is already present, modify its style
        const pTagRegex = /<p\b[^>]*>/gi;
        text = text.replace(pTagRegex, (match) => {
            if (!match.includes('style=')) {
                return match.replace('<p', '<p style="font-weight: normal"');
            } else {
                return match.replace('style="', 'style="font-weight: normal;');
            }
        });
    }

    return text;
};

const updateJson = (data: any) => {
    const updatedJson = JSON.parse(JSON.stringify(data));

    if (updatedJson.pages) {
        updatedJson.pages.forEach((page: any) => {
            if (page.elements) {
                page.elements.forEach((element: any) => {
                    if (element.title) {
                        if (typeof element.title === 'string') {
                            element.title = {
                                default: wrapInParagraphWithStyle(element.title),
                            };
                        } else {
                            if (element.title.default && typeof element.title.default === 'string') {
                                element.title.default = wrapInParagraphWithStyle(element.title.default);
                            }
                            if (element.title.da && typeof element.title.da === 'string') {
                                element.title.da = wrapInParagraphWithStyle(element.title.da);
                            }
                        }
                    }
                });
            }
        });
    }

    return updatedJson;
};

const licenseKey = process.env.REACT_APP_SURVEY_LICENSE_KEY;
SurveyCore.setLicenseKey(licenseKey as any);
nouislider(SurveyCore);
var converter = new Converter();
function doMarkdown(survey: any, options: any) {
    var str = converter.makeHtml(options.text);
    // eslint-disable-next-line eqeqeq
    if (str && str.indexOf('<p>') == 0) {
        str = str.substring(3);
        str = str.substring(0, str.length - 4);
    }

    options.html = str;
}
widgets.ckeditor(SurveyCore);

const EligibilityQuestionnairePage = () => {
    const dispatch = useDispatch();
    const token = useSelector((state: RootState) => state.global.authToken);
    const isLoading = useSelector((state: RootState) => state.global.isLoading);
    const participantDetails = useSelector((state: RootState) => state.user.participantDetails);
    const [isShow, setIsShow] = useState<boolean>(false);
    const [eligibilityStatus, setEligibilityStatus] = useState<number | null>(null);

    const matches = useMediaQuery('(min-width:580px)');

    const getDetails = useCallback(async () => {
        try {
            const participantDetailsResponse = await ParticipantAPI.getParticipantDetails(token ?? '');
            dispatch(setParticipantDetails(participantDetailsResponse));
        } catch (e) {
            console.error(e);
        }
    }, [dispatch, token]);

    useEffect(() => {
        getDetails();
    }, [getDetails]);

    const filterUnansweredQuestions = (json: any, data: any) => {
        const filteredJson = JSON.parse(JSON.stringify(json));

        if (filteredJson.pages) {
            filteredJson.pages.forEach((page: any) => {
                if (page.elements) {
                    page.elements = page.elements.filter((element: any) => {
                        if (element.name && data[element.name] !== undefined && data[element.name] !== null) {
                            return true;
                        }
                        return false;
                    });
                }
            });
        }

        return filteredJson;
    };

    function createSurveyPdfModel(surveyModel: any) {
        const pdfWidth = 210;
        const pdfHeight = 300;
        const options = {
            fontSize: 14,
            margins: {
                left: 10,
                right: 10,
                top: 10,
                bot: 10,
            },
            format: [pdfWidth, pdfHeight],
        };

        let updatedJson = JSON.parse(JSON.stringify(eligibilityQuestionnaireJSON));

        // Optionally update JSON titles
        updatedJson = updateJson(updatedJson);

        // Remove "visibleIf" conditions from the JSON
        updatedJson = JSON.stringify(updatedJson).replace(
            /("visibleIf": "[^"]+")(,)?/g,
            (_match: any, p1: any, p2: any) => (p2 ? '' : p1)
        );
        updatedJson = JSON.parse(updatedJson); // Convert back to an object after the string replace operation

        // Filter out unanswered questions based on survey data
        updatedJson = filterUnansweredQuestions(updatedJson, surveyModel.data);

        const surveyPDF: any = new SurveyPDF(updatedJson, options);
        surveyPDF.data = surveyModel.data;
        surveyPDF.onTextMarkdown.add(doMarkdown);
        surveyPDF.showInvisibleElements = true;
        surveyPDF.mode = 'display';
        return surveyPDF;
    }

    const saveSurveyToBase64 = useCallback(async (surveyModel: any) => {
        try {
            const surveyPDF = createSurveyPdfModel(surveyModel);
            const pdfBlob = await surveyPDF.raw('blob');

            return new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(pdfBlob);
                reader.onloadend = () => {
                    const base64String = reader.result as string;
                    resolve(base64String.split(',')[1]);
                };
                reader.onerror = reject;
            });
        } catch (error: any) {
            console.error('Error ', error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const surveyComplete = useCallback(
        async (surveyData: Model) => {
            if (surveyData.data.Save_OK === false) {
                window.location.href = 'https://planetdiet.dk/';
            } else {
                try {
                    if (participantDetails) {
                        const participantId = participantDetails?.participantId;
                        dispatch(loadingStart());
                        const base64PDF = await saveSurveyToBase64(surveyData);
                        await QuestionnaireAPI.uploadScreeningQuestionnaire(
                            participantId ?? '',
                            base64PDF ?? '',
                            token ?? ''
                        );
                        const surveyResponseJson = JSON.stringify(surveyData.data);
                        const response = await ParticipantAPI.calculateParticipantEligibility(
                            { surveyResponseJson: surveyResponseJson },
                            token ?? ''
                        );
                        setEligibilityStatus(response.eligibility);
                        setIsShow(true);
                    }
                } finally {
                    dispatch(loadingEnd());
                }
            }
        },
        [participantDetails, dispatch, saveSurveyToBase64, token]
    );

    const onBook = useCallback(async () => {
        try {
            const participantViewStatus: any = await ParticipantAPI.getParticipantViewStatus(token ?? '');
            dispatch(setParticipantViewState(participantViewStatus.data.viewState));
        } catch (error: any) {
            dispatch(setViewStateErrorText(error.response.data.message));
            dispatch(setViewStateErrorCode(error.response.data.statusCode));
            dispatch(setParticipantViewState(ViewState.SERVER_ERROR));
        }
    }, [dispatch, token]);

    const onGoPlanetdiet = useCallback(async () => {
        window.location.href = 'https://planetdiet.dk/';
    }, []);

    const survey = useMemo(() => new Model(eligibilityQuestionnaireJSON), []);
    const theme: any = light_theme_survey;
    survey.applyTheme(theme);
    survey.onComplete.add(surveyComplete);

    useMemo(() => {
        // const converter = new Converter();
        survey.onTextMarkdown.add(function (_survey: any, options) {
            let str = converter.makeHtml(options.text);
            // eslint-disable-next-line eqeqeq
            if (str && str.indexOf('<p>') == 0) {
                str = str.substring(3);
                str = str.substring(0, str.length - 4);
            }
            options.html = str;
        });
    }, [survey.onTextMarkdown]);

    return (
        <div className={styles.container}>
            <div
                className={styles.content}
                style={{
                    width: matches ? '80vw' : '100%',
                }}
            >
                {isLoading && (
                    <div className={styles.loadingBlocks}>
                        <Skeleton variant={'rounded'} loading={isLoading}>
                            <div className={styles.loadingTitle} />
                        </Skeleton>
                        <Skeleton variant={'rounded'} loading={isLoading}>
                            <div className={styles.loadingText} />
                        </Skeleton>
                        <Skeleton variant={'rounded'} loading={isLoading}>
                            <div className={styles.loadingTextShort} />
                        </Skeleton>
                        <Skeleton variant={'rounded'} loading={isLoading}>
                            <div className={styles.loadingTextShort} />
                        </Skeleton>
                        <Skeleton variant={'rounded'} loading={isLoading}>
                            <div className={styles.loadingButton} />
                        </Skeleton>
                    </div>
                )}
                {!isShow && !isLoading && <Survey model={survey} />}
                {isShow && !isLoading && (
                    <div>
                        <h2>PLANETDIET spørgeskema</h2>
                        {eligibilityStatus === 1 && (
                            <p>
                                        Tak for din interesse i Projekt PLANETDIET! Baseret på dine svar kan du desværre ikke
                                        deltage.
                            </p>
                        )}
                        {eligibilityStatus === 2 && (
                            <p>
                                        Mange tak! Baseret på dine svar kan vi se, at du kan deltage i Projekt PLANETDIET om 18
                                        måender. Dette skyldes, at du enten er gravid, ammer eller er i fertilitetsbehandling.
                                        Vi sender dig en ny invitation i din Digitale post om 18 måneder.
                            </p>
                        )}
                        {eligibilityStatus === 3 && (
                            <p>
                                        Mange tak! Baseret på dine svar kan vi se, at du kan deltage i Projekt PLANETDIET om 3
                                        måneder. Dette skyldes, at du for nyligt har fået en ny diagnose, afsluttet en
                                        behandling eller er blevet opereret. Vi sender dig en ny invitation i din Digitale post
                                        om 3 måneder.
                            </p>
                        )}
                        {eligibilityStatus === 4 && (
                            <p>
                                {isOverload ? (
                                    <OverloadEligibleText />
                                ) : (
                                    'Mange tak! Baseret på dine svar kan vi se, at du kan deltage i PlanetDiet-studiet. Det næste du skal gøre, er at booke tid til et informationsmøde. Følg linket for at booke din tid:'
                                )}
                            </p>
                        )}
                        {eligibilityStatus === 4 ? (
                            <Button
                                className={styles.button}
                                variant={'outlined'}
                                sx={{ color: colors.primary }}
                                onClick={onBook}
                            >
                                        Gå til booking
                            </Button>
                        ) : (
                            <Button
                                className={styles.button}
                                variant={'outlined'}
                                sx={{ color: colors.primary }}
                                onClick={onGoPlanetdiet}
                            >
                                        Ok
                            </Button>
                        )}
                    </div>
                )}
            </div>
            <div className={styles.footer}>
                <span className={styles.footerText}>VERSION 3 18.08.25</span>
            </div>
        </div>
    );
};

const styles = {
    container: css({
        marginTop: '4.5rem',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '92vh',
        width: '100%',
        fontFamily: 'Figtree !important',
        '.sd-page': {
            fontFamily: 'Figtree !important',
        },
        '.sd-header__text .sd-title': {
            fontFamily: 'Figtree !important',
        },
        '.sd-root-modern .sd-container-modern__title .sd-description': {
            fontFamily: 'Figtree !important',
        },
        '.sd-page .sd-page__description': {
            fontFamily: 'Figtree !important',
        },
        '.sd-root-modern': {
            fontFamily: 'Figtree !important',
        },
        '.sd-title.sd-element__title': {
            fontFamily: 'Figtree !important',
        },
        '.sd-description.sd-question__description--under-input': {
            fontFamily: 'Figtree !important',
        },
        '.sd-item__control-label': {
            fontFamily: 'Figtree !important',
        },
        '.sd-completedpage h3, .sd-completed-before-page h3': {
            fontFamily: 'Figtree !important',
        },
        '.sd-boolean__label': {
            fontFamily: 'Figtree !important',
        },
        '.sd-root-modern .sd-container-modern__title': {
            background: colors.backgroundLight,
        },
        '.sd-root-modern__wrapper': {
            background: colors.backgroundLight,
        },
        '.sd-body__navigation .sd-btn': {
            background: colors.primary,
            color: colors.primaryTextLight,
            fontFamily: 'Figtree !important',
        },
        '.noUi-connect, .progress-bar': {
            backgroundColor: 'rgb(29, 41, 81)',
        },
    }),
    content: css({
        flex: 1,
        margin: '0 auto',
        display: 'flex',
    }),
    loadingBlocks: css({
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        margin: '1rem 0',
        width: '100%',
    }),
    loadingTitle: css({
        height: '2rem',
        width: '22rem',
    }),
    loadingText: css({
        height: '1rem',
        width: '44rem',
    }),
    loadingTextShort: css({
        height: '1rem',
        width: '32rem',
    }),
    loadingButton: css({
        height: '2rem',
        width: '11.4375rem',
        borderRadius: '3.125rem',
        marginTop: '0.75rem',
    }),
    footer: css({
        height: '1.5rem',
    }),
    footerText: css({
        color: '#605E5C',
        fontFamily: 'Segoe UI',
        fontSize: '0.8125rem',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '1.25rem',
        marginLeft: '5rem',
    }),
    button: css({
        borderRadius: '3.125rem !important',
        height: '2rem !important',
        width: '11.4375rem !important',
    }),
};

export default EligibilityQuestionnairePage;
